/**
 * Shared sandbox build logic for preview and reliability harness.
 * React/react-dom are resolved locally via host-provided globals (no network for React).
 *
 * PatchGuard blocks explicit esm.sh URL imports in generated code (PATCH_GUARD_FORBIDDEN_IMPORT).
 * SandboxBuild may use a CDN (esm.sh) internally for dependency resolution of allowlisted packages
 * (e.g. react-router-dom); do not treat that as a user-code violation.
 *
 * All normalization (including ensureScreenStubs) runs IN-MEMORY ONLY during sandbox preview build.
 * It never persists to project DB/storage; only mutates the passed FileMap in-place.
 *
 * In the browser we use esbuild-wasm; in Node (CLI) we use native esbuild for the same build API.
 */
import * as esbuildWasm from "esbuild-wasm";
import { MULTI_PAGE_ROUTER_TS, MULTI_PAGE_STORAGE_TS } from "./scaffoldFallbacks";
import { ALL_SANDBOX_ALLOWED_PACKAGES, getPackageName, isAllowedPackage } from "./allowedPackages";

type EsbuildLike = typeof esbuildWasm;
let esbuild: EsbuildLike = esbuildWasm;

export type FileMap = Record<string, string>;

/**
 * Strip ALL CSS imports from ALL JS/TS files — esbuild-wasm can't handle CSS imports
 * in JS files without an output path configured. The CSS from index.css is prepended
 * to the build output separately.
 *
 * This handles:
 * - import "./index.css"
 * - import "./App.css"
 * - import "../styles/global.css"
 * - import "@/styles/theme.css"
 */
function stripAllCssImports(files: FileMap): void {
  // Regex to match any CSS import statement
  const cssImportRegex = /^\s*import\s+['"][^'"]*\.css['"]\s*;?\s*$/gm;

  for (const key of Object.keys(files)) {
    // Only process JS/TS files
    if (!/\.(tsx?|jsx?|mjs)$/.test(key)) continue;
    const content = files[key];
    if (typeof content !== "string") continue;

    const stripped = content.replace(cssImportRegex, "// [sandbox] CSS import removed");
    if (stripped !== content) {
      files[key] = stripped;
      console.log(`[sandbox] stripAllCssImports: removed CSS import from ${key}`);
    }
  }
}

/** Collapse ".." and "." in path so src/pages/../lib/storage → src/lib/storage. */
function collapsePathDots(path: string): string {
  const parts = path.split("/");
  const out: string[] = [];
  for (const part of parts) {
    if (part === "..") out.pop();
    else if (part !== "." && part !== "") out.push(part);
  }
  return out.join("/");
}

/**
 * Resolve import path to src/screens/*. Returns null if not a screen import.
 * STRICT ALLOWLIST: only paths under src/screens/ ending .ts/.tsx (prefer .tsx).
 * Rejects: .. in path, paths resolving outside src/screens/, .js/.jsx (no stub creation).
 */
function resolveScreenPath(importPath: string, importerDir: string): string | null {
  const raw = importPath.trim();
  if (raw.includes("..")) return null;
  let p: string;
  if (raw.startsWith("@/")) p = "src/" + raw.slice(2);
  else if (raw.startsWith("./") || raw.startsWith("../")) {
    const base = importerDir.replace(/\/?$/, "");
    p = collapsePathDots((base ? base + "/" : "") + raw);
  } else return null;
  if (!p.startsWith("src/screens/")) return null;
  if (p === "src/screens" || p.endsWith("/")) return null;
  if (/\.(jsx|js)$/.test(p)) return null;
  if (!/\.(tsx|ts)$/.test(p)) p += ".tsx";
  const stubPath = p.replace(/\.ts$/, ".tsx");
  if (!/^src\/screens\/[^/]+\.tsx$/.test(stubPath)) return null;
  return stubPath;
}

/** Extract screen imports under src/screens/* from file content. */
function extractScreenImports(content: string, importerPath: string): string[] {
  const imports: string[] = [];
  const importerDir = importerPath.includes("/") ? importerPath.slice(0, importerPath.lastIndexOf("/")) : "";
  const re = /from\s+["']([^"']+)["']/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    const resolved = resolveScreenPath(m[1], importerDir);
    if (resolved) imports.push(resolved);
  }
  return imports;
}

/** Create stub .tsx for missing screen. Named + default export for compatibility. */
function createScreenStub(componentPath: string): string {
  const base = componentPath.replace(/\.tsx$/, "");
  const name = base.split("/").pop() || "Screen";
  const displayName = name.replace(/^[a-z]/, (c) => c.toUpperCase());
  return `import React from "react";\n\nexport function ${displayName}() {\n  return <div>${displayName}</div>;\n}\nexport default ${displayName};\n`;
}

/**
 * Ensure imported src/screens/*.tsx files exist; create stubs for missing ones.
 * IN-MEMORY ONLY: runs during sandbox preview normalization. Never persists to DB/storage.
 * Returns list of created stub paths for debug/UI warning.
 */
function ensureScreenStubs(files: FileMap): string[] {
  const created: string[] = [];
  const needed = new Set<string>();
  for (const [path, content] of Object.entries(files)) {
    if (typeof content !== "string") continue;
    if (!/\.(tsx|ts|jsx|js)$/.test(path)) continue;
    for (const importPath of extractScreenImports(content, path)) {
      if (importPath) needed.add(importPath);
    }
  }
  const exts = [".tsx", ".ts", ".jsx", ".js"];
  for (const p of needed) {
    const base = p.replace(/\.(tsx|ts|jsx|js)$/, "");
    const tsxPath = base + ".tsx";
    const exists = exts.some((e) => files[base + e] != null);
    if (!exists) {
      files[tsxPath] = createScreenStub(tsxPath);
      created.push(tsxPath);
    }
  }
  return created;
}

/**
 * Resolve import path to src/pages/*. Returns null if not a page import.
 * Parallel to resolveScreenPath but for src/pages/.
 */
function resolvePagePath(importPath: string, importerDir: string): string | null {
  const raw = importPath.trim();
  if (raw.includes("..")) return null;
  let p: string;
  if (raw.startsWith("@/")) p = "src/" + raw.slice(2);
  else if (raw.startsWith("./") || raw.startsWith("../")) {
    const base = importerDir.replace(/\/?$/, "");
    p = collapsePathDots((base ? base + "/" : "") + raw);
  } else return null;
  if (!p.startsWith("src/pages/")) return null;
  if (p === "src/pages" || p.endsWith("/")) return null;
  if (/\.(jsx|js)$/.test(p)) return null;
  if (!/\.(tsx|ts)$/.test(p)) p += ".tsx";
  const stubPath = p.replace(/\.ts$/, ".tsx");
  if (!/^src\/pages\/[^/]+\.tsx$/.test(stubPath)) return null;
  return stubPath;
}

/** Extract page imports under src/pages/* from file content. */
function extractPageImports(content: string, importerPath: string): string[] {
  const imports: string[] = [];
  const importerDir = importerPath.includes("/") ? importerPath.slice(0, importerPath.lastIndexOf("/")) : "";
  const re = /from\s+["']([^"']+)["']/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    const resolved = resolvePagePath(m[1], importerDir);
    if (resolved) imports.push(resolved);
  }
  return imports;
}

/** Create stub .tsx for missing page. Named + default export for compatibility. */
function createPageStub(componentPath: string): string {
  const base = componentPath.replace(/\.tsx$/, "");
  const name = base.split("/").pop() || "Page";
  const displayName = name.replace(/^[a-z]/, (c) => c.toUpperCase());
  return `import React from "react";\n\nexport function ${displayName}() {\n  return <div>${displayName}</div>;\n}\nexport default ${displayName};\n`;
}

/**
 * Ensure imported src/pages/*.tsx files exist; create stubs for missing ones.
 * IN-MEMORY ONLY: runs during sandbox preview normalization. Never persists to DB/storage.
 * Returns list of created stub paths for debug/UI warning.
 */
function ensurePageStubs(files: FileMap): string[] {
  const created: string[] = [];
  const needed = new Set<string>();
  for (const [path, content] of Object.entries(files)) {
    if (typeof content !== "string") continue;
    if (!/\.(tsx|ts|jsx|js)$/.test(path)) continue;
    for (const importPath of extractPageImports(content, path)) {
      if (importPath) needed.add(importPath);
    }
  }
  const exts = [".tsx", ".ts", ".jsx", ".js"];
  for (const p of needed) {
    const base = p.replace(/\.(tsx|ts|jsx|js)$/, "");
    const tsxPath = base + ".tsx";
    const exists = exts.some((e) => files[base + e] != null);
    if (!exists) {
      files[tsxPath] = createPageStub(tsxPath);
      created.push(tsxPath);
    }
  }
  return created;
}

/** Resolve import path to src/components/* file path. Returns null if not a component import. */
function resolveComponentPath(importPath: string, importerDir: string): string | null {
  let p = importPath.trim();
  if (p.startsWith("@/")) p = "src/" + p.slice(2);
  else if (p.startsWith("./") || p.startsWith("../")) {
    const base = importerDir.replace(/\/?$/, "");
    p = collapsePathDots((base ? base + "/" : "") + p);
  } else return null;
  if (!p.startsWith("src/components/")) return null;
  if (!p.endsWith(".tsx") && !p.endsWith(".ts") && !p.endsWith(".jsx") && !p.endsWith(".js")) p += ".tsx";
  return p;
}

/** Extract component imports under src/components/* from file content. */
function extractComponentImports(content: string, importerPath: string): string[] {
  const imports: string[] = [];
  const importerDir = importerPath.includes("/") ? importerPath.slice(0, importerPath.lastIndexOf("/")) : "";
  const re = /from\s+["']([^"']+)["']/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    const resolved = resolveComponentPath(m[1], importerDir);
    if (resolved) imports.push(resolved);
  }
  return imports;
}

/** Create stub .tsx for missing component. Named + default export so both `import { X }` and `import X` work. */
function createComponentStub(componentPath: string): string {
  const base = componentPath.replace(/\.tsx$/, "");
  const name = base.split("/").pop() || "Component";
  const displayName = name.replace(/^[a-z]/, (c) => c.toUpperCase());
  return `import React from "react";\n\nexport function ${displayName}() {\n  return <div>${displayName}</div>;\n}\nexport default ${displayName};\n`;
}

/** Ensure all imported src/components/* files exist; create stubs for missing ones. */
function ensureComponentStubs(files: FileMap): void {
  const needed = new Set<string>();
  for (const [path, content] of Object.entries(files)) {
    if (typeof content !== "string") continue;
    if (!/\.(tsx|ts|jsx|js)$/.test(path)) continue;
    for (const importPath of extractComponentImports(content, path)) {
      needed.add(importPath);
    }
  }
  const exts = [".tsx", ".ts", ".jsx", ".js"];
  for (const p of needed) {
    const base = p.replace(/\.(tsx|ts|jsx|js)$/, "");
    const tsxPath = base + ".tsx";
    const exists = exts.some((e) => files[base + e]);
    if (!exists) {
      files[tsxPath] = createComponentStub(tsxPath);
    }
  }
}

/** Inject multi-page scaffold lib files if missing. Content-aware: only inject when some file imports them. */
function ensureMultiPageLibs(files: FileMap): void {
  let needsRouter = false;
  let needsStorage = false;
  for (const content of Object.values(files)) {
    if (typeof content !== "string") continue;
    if (content.includes("../lib/storage") || content.includes("@/lib/storage")) needsStorage = true;
    if (content.includes("../lib/router") || content.includes("@/lib/router")) needsRouter = true;
  }
  if (needsStorage && !files["src/lib/storage.ts"]) files["src/lib/storage.ts"] = MULTI_PAGE_STORAGE_TS;
  if (needsRouter && !files["src/lib/router.ts"]) files["src/lib/router.ts"] = MULTI_PAGE_ROUTER_TS;
}

/** Map of EVERY named UI export to its correct direct-import path. */
const BARREL_TO_DIRECT: Record<string, string> = {
  // button
  Button: "@/components/ui/button",
  // card
  Card: "@/components/ui/card",
  CardHeader: "@/components/ui/card",
  CardTitle: "@/components/ui/card",
  CardDescription: "@/components/ui/card",
  CardContent: "@/components/ui/card",
  CardFooter: "@/components/ui/card",
  // input
  Input: "@/components/ui/input",
  // textarea
  Textarea: "@/components/ui/textarea",
  // badge
  Badge: "@/components/ui/badge",
  // table
  Table: "@/components/ui/table",
  TableHeader: "@/components/ui/table",
  TableBody: "@/components/ui/table",
  TableRow: "@/components/ui/table",
  TableHead: "@/components/ui/table",
  TableCell: "@/components/ui/table",
  // select
  Select: "@/components/ui/select",
  // dialog
  Dialog: "@/components/ui/dialog",
  DialogContent: "@/components/ui/dialog",
  DialogHeader: "@/components/ui/dialog",
  DialogTitle: "@/components/ui/dialog",
  DialogDescription: "@/components/ui/dialog",
  DialogFooter: "@/components/ui/dialog",
  DialogTrigger: "@/components/ui/dialog",
  // tabs
  Tabs: "@/components/ui/tabs",
  TabsList: "@/components/ui/tabs",
  TabsTrigger: "@/components/ui/tabs",
  TabsContent: "@/components/ui/tabs",
  // alert
  Alert: "@/components/ui/alert",
  AlertTitle: "@/components/ui/alert",
  AlertDescription: "@/components/ui/alert",
  // avatar
  Avatar: "@/components/ui/avatar",
  AvatarImage: "@/components/ui/avatar",
  AvatarFallback: "@/components/ui/avatar",
  // separator
  Separator: "@/components/ui/separator",
};

/**
 * Rewrite imports from @/components/ui/* to correct direct component paths.
 * Handles:
 *   1. Barrel imports: import { Card, Badge } from "@/components/ui"
 *   2. Cross-file imports: import { Card, Badge } from "@/components/ui/card" (Badge is in badge.tsx)
 *   3. Mixed: import { CardTitle, Badge, Button } from "@/components/ui/card"
 */
function rewriteBarrelImportsToDirect(content: string): string {
  // Match ANY import from @/components/ui or @/components/ui/anything or src/components/ui variants
  const uiImportRegex = /import\s*\{([^}]+)\}\s*from\s*["']((?:@\/|src\/)components\/ui(?:\/\w+)?)(\.tsx)?["']\s*;?/g;
  return content.replace(uiImportRegex, (_, names: string) => {
    const specs = names.split(",").map((s: string) => s.trim()).filter(Boolean);
    // Group specs by their correct target file
    const grouped = new Map<string, string[]>();
    for (const spec of specs) {
      const baseName = spec.replace(/\s+as\s+\w+$/, "").trim();
      const target = BARREL_TO_DIRECT[baseName] ?? `@/components/ui/${baseName.toLowerCase()}`;
      const arr = grouped.get(target) ?? [];
      arr.push(spec);
      grouped.set(target, arr);
    }
    return [...grouped.entries()]
      .map(([target, specList]) => `import { ${specList.join(", ")} } from "${target}";`)
      .join("\n") + "\n";
  });
}

/** Rewrite default App import to namespace + fallback in entry files so build never fails on missing default. */
function ensureEntryAppImportIsSafe(files: FileMap): void {
  const entryKeys = ["src/main.tsx", "src/main.jsx", "src/main.ts", "src/index.tsx", "src/index.jsx"];
  const defaultAppRe = /import\s+App\s+from\s+["']([^"']+)["']\s*;?/g;
  const replacement = (path: string) =>
    `import * as AppModule from "${path}";\nconst App = (AppModule as any).default ?? (AppModule as any).App;`;
  for (const key of entryKeys) {
    const content = files[key];
    if (typeof content !== "string") continue;
    const next = content.replace(defaultAppRe, (_, path) => replacement(path));
    if (next !== content) files[key] = next;
  }
}

/** Ensure src/App.tsx has default export when it only has named export. AI sometimes default-imports App. */
function ensureAppDefaultExport(files: FileMap): void {
  const appPath = "src/App.tsx";
  const content = files[appPath];
  if (typeof content !== "string") return;
  if (/export\s+default\s+/m.test(content)) return;
  if (/export\s+(function|const)\s+App\b/m.test(content)) {
    files[appPath] = content.trimEnd() + "\nexport default App;\n";
  }
}

/**
 * Add named re-export when a module has default export only. Fixes "No matching export for import X"
 * when App.tsx uses `import { Shell }` but Shell.tsx has `export default function Shell`.
 * Only adds if the named export is not already present.
 */
function ensureNamedReExportsForDefaultOnly(files: FileMap): void {
  for (const [path, content] of Object.entries(files)) {
    if (typeof content !== "string") continue;
    if (!/\.(tsx|ts|jsx|js)$/.test(path) || !path.startsWith("src/")) continue;
    let exportName: string | null = null;
    const m1 = content.match(/export\s+default\s+function\s+(\w+)\s*[(<]/);
    if (m1) exportName = m1[1];
    else {
      const m2 = content.match(/export\s+default\s+(\w+)\s*;/);
      if (m2) exportName = m2[1];
    }
    if (!exportName) continue;
    if (new RegExp(`export\\s+{\\s*${exportName}\\s*}`).test(content)) continue;
    files[path] = content.trimEnd() + `\nexport { ${exportName} };\n`;
  }
}

/** Ensure src/screens/*.tsx files with named export also have default export (App often default-imports). Exported for reuse. */
export function ensureScreenDefaultExports(files: FileMap): void {
  for (const [path, content] of Object.entries(files)) {
    if (typeof content !== "string" || !path.match(/^src\/screens\/[^/]+\.tsx$/)) continue;
    if (/export\s+default\s+/m.test(content)) continue;
    const name = path.replace(/^.*\//, "").replace(/\.tsx$/, "");
    const displayName = name.replace(/^[a-z]/, (c) => c.toUpperCase());
    if (new RegExp(`export\\s+(function|const)\\s+${displayName}\\b`).test(content)) {
      files[path] = content.trimEnd() + `\nexport default ${displayName};\n`;
    }
  }
}

/** Ensure src/pages/*.tsx files with named export also have default export (App often default-imports pages). */
function ensurePageDefaultExports(files: FileMap): void {
  for (const [path, content] of Object.entries(files)) {
    if (typeof content !== "string" || !path.match(/^src\/pages\/[^/]+\.tsx$/)) continue;
    if (/export\s+default\s+/m.test(content)) continue;
    const name = path.replace(/^.*\//, "").replace(/\.tsx$/, "");
    const displayName = name.replace(/^[a-z]/, (c) => c.toUpperCase());
    if (new RegExp(`export\\s+(function|const)\\s+${displayName}\\b`).test(content)) {
      files[path] = content.trimEnd() + `\nexport default ${displayName};\n`;
    }
  }
}

/** Rewrite barrel imports in all ts/tsx files so build resolves correctly. */
function ensureDirectUiImports(files: FileMap): void {
  for (const [path, content] of Object.entries(files)) {
    if (typeof content !== "string" || !/\.(tsx|ts|jsx|js)$/.test(path)) continue;
    if (!content.includes("components/ui") && !content.includes("components/ui.tsx")) continue;
    const rewritten = rewriteBarrelImportsToDirect(content);
    if (rewritten !== content) files[path] = rewritten;
  }
}

/** Extensions that get extensionless aliases (TS/JS modules only; not .css/.json). */
const TS_JS_EXTENSIONS = [".tsx", ".ts", ".jsx", ".js"];

function normPath(p: string): string {
  let s = String(p ?? "").trim().replace(/\\/g, "/");
  if (s.startsWith("./")) s = s.slice(2);
  if (s.startsWith("/")) s = s.slice(1);
  return s;
}

/** Resolve requested path at load time: exact, +ext, +/index+ext, case-insensitive. Only resolves to existing keys. Exported for tests. */
export function resolveFileKeyForLoad(
  requestedKey: string,
  files: FileMap
): string | null {
  const norm = normPath(requestedKey);
  if (files[norm] != null) return norm;
  for (const ext of TS_JS_EXTENSIONS) {
    const k = norm + ext;
    if (files[k] != null) return k;
  }
  for (const ext of TS_JS_EXTENSIONS) {
    const k = norm + "/index" + ext;
    if (files[k] != null) return k;
  }
  const lowerToCanonical = new Map<string, string>();
  for (const k of Object.keys(files)) {
    const n = normPath(k);
    lowerToCanonical.set(n.toLowerCase(), k);
  }
  const normLower = norm.toLowerCase();
  if (lowerToCanonical.has(normLower)) return lowerToCanonical.get(normLower)!;
  for (const ext of TS_JS_EXTENSIONS) {
    const k = (norm + ext).toLowerCase();
    if (lowerToCanonical.has(k)) return lowerToCanonical.get(k)!;
  }
  for (const ext of TS_JS_EXTENSIONS) {
    const k = (norm + "/index" + ext).toLowerCase();
    if (lowerToCanonical.has(k)) return lowerToCanonical.get(k)!;
  }
  return null;
}

/**
 * Add extensionless alias entries for .ts/.tsx/.js/.jsx so the resolver can find
 * "src/App" when only "src/App.tsx" exists (esbuild may request extensionless paths).
 * Exported for unit tests.
 */
export function addExtensionlessAliases(files: FileMap): void {
  for (const path of Object.keys(files)) {
    for (const ext of TS_JS_EXTENSIONS) {
      if (path.endsWith(ext)) {
        const alias = path.slice(0, -ext.length);
        if (alias && files[alias] === undefined) {
          files[alias] = files[path]!;
        }
        break;
      }
    }
  }
}

/** Normalize file map for DayOne sandbox: no CSS import, required libs present, stub missing components. */
export function normalizeFileMapForSandbox(files: FileMap): { createdScreenStubs: string[] } {
  stripAllCssImports(files);
  ensureDirectUiImports(files);
  ensureEntryAppImportIsSafe(files);
  ensureAppDefaultExport(files);
  ensureScreenDefaultExports(files);
  ensurePageDefaultExports(files);
  ensureNamedReExportsForDefaultOnly(files);
  ensureMultiPageLibs(files);
  ensureComponentStubs(files);
  const createdScreenStubs = ensureScreenStubs(files);
  const createdPageStubs = ensurePageStubs(files);
  return { createdScreenStubs: [...createdScreenStubs, ...createdPageStubs] };
}

declare global {
  // eslint-disable-next-line no-var
  var __dayoneEsbuildInitPromise: Promise<void> | undefined;
}

function normalizePath(p: string): string {
  let s = String(p ?? "").trim();
  if (s.startsWith("./")) s = s.slice(2);
  if (s.startsWith("/")) s = s.slice(1);
  return s;
}

function loaderFor(path: string): esbuildWasm.Loader {
  if (path.endsWith(".tsx")) return "tsx";
  if (path.endsWith(".ts")) return "ts";
  if (path.endsWith(".jsx")) return "jsx";
  if (path.endsWith(".js")) return "js";
  if (path.endsWith(".css")) return "css";
  if (path.endsWith(".json")) return "json";
  return "text";
}

export function detectEntry(files: FileMap): string | null {
  if (files["src/__dayone_boot.tsx"]) return "src/__dayone_boot.tsx";
  return (
    [
      "src/main.tsx",
      "src/index.tsx",
      "src/main.jsx",
      "src/index.jsx",
      "src/main.ts",
      "src/index.ts",
      "src/main.js",
      "src/index.js",
    ].find((f) => files[f]) || null
  );
}

function getEsbuildWasmURL(): string {
  if (typeof import.meta.env !== "undefined" && import.meta.env?.BASE_URL != null) {
    return `${import.meta.env.BASE_URL}esbuild.wasm`;
  }
  return new URL("../../node_modules/esbuild-wasm/esbuild.wasm", import.meta.url).href;
}

export async function ensureEsbuild(): Promise<void> {
  if (!globalThis.__dayoneEsbuildInitPromise) {
    globalThis.__dayoneEsbuildInitPromise = (async () => {
      if (typeof window !== "undefined") {
        await esbuildWasm.initialize({
          wasmURL: getEsbuildWasmURL(),
          worker: true,
        });
        esbuild = esbuildWasm;
      } else {
        const native = await import("esbuild");
        esbuild = native as unknown as EsbuildLike;
      }
    })();
  }
  await globalThis.__dayoneEsbuildInitPromise;
}

const httpCache = new Map<string, { contents: string; loader: esbuildWasm.Loader }>();

/**
 * Pre-warm the HTTP cache with common large dependencies.
 * Called before buildSandbox to reduce latency during bundling.
 * Fetches happen in parallel with a concurrency limit.
 */
const PREWARM_URLS: string[] = [
  // React Router (jsDelivr)
  "https://cdn.jsdelivr.net/npm/react-router-dom@6.30.3/dist/index.js?module",
  "https://cdn.jsdelivr.net/npm/react-router@6.30.3/dist/index.js?module",
  "https://cdn.jsdelivr.net/npm/@remix-run/router@1.21.0/dist/router.js?module",
  // Large deps (esm.sh with pinned versions)
  "https://esm.sh/lucide-react@0.469.0?bundle&target=es2020&dev",
  "https://esm.sh/@supabase/supabase-js@2.49.4?bundle&target=es2020&dev",
  "https://esm.sh/framer-motion@6.5.1?bundle&target=es2020&dev",
  "https://esm.sh/date-fns@3.6.0?bundle&target=es2020&dev",
];

let prewarmStarted = false;
let prewarmPromise: Promise<void> | null = null;

/**
 * Start prewarming CDN dependencies in background.
 * Safe to call multiple times — only runs once.
 */
export function startDependencyPrewarm(): void {
  if (prewarmStarted) return;
  prewarmStarted = true;

  prewarmPromise = (async () => {
    const CONCURRENCY = 3;
    const pending: Promise<void>[] = [];

    for (const url of PREWARM_URLS) {
      if (httpCache.has(url)) continue;

      const fetchOne = async () => {
        try {
          const res = await fetch(url);
          if (!res.ok) return;
          const contents = await res.text();
          const contentType = res.headers.get("content-type");
          const loader = contentType?.includes("css") ? "css" as const : "js" as const;
          httpCache.set(url, { contents, loader });
        } catch {
          // Silently ignore prewarm failures — build will retry
        }
      };

      pending.push(fetchOne());

      if (pending.length >= CONCURRENCY) {
        await Promise.race(pending);
        // Remove settled promises
        for (let i = pending.length - 1; i >= 0; i--) {
          const p = pending[i];
          // Check if settled by racing against immediate resolve
          const settled = await Promise.race([p.then(() => true), Promise.resolve(false)]);
          if (settled) pending.splice(i, 1);
        }
      }
    }

    await Promise.allSettled(pending);
    console.log("[sandbox] prewarm complete, cache size:", httpCache.size);
  })();
}

/** Wait for prewarming to complete (with timeout). */
async function awaitPrewarm(timeoutMs = 5000): Promise<void> {
  if (!prewarmPromise) return;
  await Promise.race([
    prewarmPromise,
    new Promise<void>((r) => setTimeout(r, timeoutMs)),
  ]);
}

/**
 * Bare specifiers allowed to resolve (e.g. via http-url).
 * React family is always local; others blocked unless in ALL_SANDBOX_ALLOWED_PACKAGES.
 *
 * Source of truth: src/lib/allowedPackages.ts
 */
const EXTERNAL_DEPENDENCY_ALLOWLIST: readonly string[] = ALL_SANDBOX_ALLOWED_PACKAGES;

export const EXTERNAL_DEPENDENCY_BLOCKED_PREFIX = "[sandbox] External dependency not allowed:";

function isAllowlisted(spec: string): boolean {
  return isAllowedPackage(spec);
}

/**
 * react-router-dom, react-router, @remix-run/router: use jsDelivr ESM (not esm.sh).
 * esm.sh does not reliably preserve named exports (BrowserRouter, Routes, Route) for these packages.
 * jsDelivr serves the package dist/index.js which preserves the export shape.
 * We pin versions to match project dependencies (package.json).
 */
const REACT_ROUTER_VERSION = "6.30.3";
const REMIX_ROUTER_VERSION = "1.21.0"; // compatible with react-router-dom@6.30.x
const JSDELIVR_BASE = "https://cdn.jsdelivr.net/npm";

/**
 * Pinned versions for all esm.sh dependencies.
 * Unpinned deps cause random preview failures when esm.sh serves different versions.
 */
const PINNED_VERSIONS: Record<string, string> = {
  "@supabase/supabase-js": "2.49.4",
  "@stripe/stripe-js": "4.6.0",
  "framer-motion": "6.5.1",
  "lucide-react": "0.469.0",
  "clsx": "2.1.1",
  "zustand": "4.5.5",
  "immer": "10.1.1",
  "jotai": "2.10.3",
  "valtio": "1.13.2",
  "date-fns": "3.6.0",
  "jspdf": "2.5.2",
  "html2canvas": "1.4.1",
  "xlsx": "0.18.5",
  "papaparse": "5.4.1",
  "react-to-print": "3.0.2",
  "recharts": "2.13.3",
  "tailwind-merge": "2.5.4",
};

function isJsdelivrRouterFamily(pkgOrUrl: string): boolean {
  const pkg = getPackageName(pkgOrUrl);
  const u = pkgOrUrl.toLowerCase();
  return (
    pkg === "react-router-dom" ||
    pkg === "react-router" ||
    pkg === "@remix-run/router" ||
    (u.includes("esm.sh") && (u.includes("react-router-dom") || u.includes("react-router") || u.includes("remix-run/router")))
  );
}

function jsdelivrRouterUrl(pkg: string): string {
  const name = getPackageName(pkg);
  const version = name === "@remix-run/router" ? REMIX_ROUTER_VERSION : REACT_ROUTER_VERSION;
  const distFile = name === "@remix-run/router" ? "router.js" : "index.js";
  return `${JSDELIVR_BASE}/${name}@${version}/dist/${distFile}?module`;
}

/** Inline shims for React family: read from host-provided globals (no network). */
const REACT_LOCAL_SHIMS: Record<string, string> = {
  // React 18 complete shim — all hooks, utilities, and concurrent features
  "react": `const __r=(typeof window!=="undefined"&&window.__DAYONE_REACT__)||{};export default __r;export const useState=__r.useState,useEffect=__r.useEffect,useRef=__r.useRef,useMemo=__r.useMemo,useCallback=__r.useCallback,useContext=__r.useContext,useLayoutEffect=__r.useLayoutEffect,useImperativeHandle=__r.useImperativeHandle,useDebugValue=__r.useDebugValue,createContext=__r.createContext,createRef=__r.createRef,forwardRef=__r.forwardRef,memo=__r.memo,Fragment=__r.Fragment,StrictMode=__r.StrictMode,createElement=__r.createElement,cloneElement=__r.cloneElement,isValidElement=__r.isValidElement,Children=__r.Children,Component=__r.Component,PureComponent=__r.PureComponent,lazy=__r.lazy,Suspense=__r.Suspense,startTransition=__r.startTransition,useTransition=__r.useTransition,useSyncExternalStore=__r.useSyncExternalStore,useId=__r.useId,useDeferredValue=__r.useDeferredValue,version=__r.version;`,
  // ReactDOM shim — includes createPortal for modals/tooltips (recharts, radix, headlessui)
  "react-dom": `const __d=(typeof window!=="undefined"&&window.__DAYONE_REACT_DOM__)||{};export default __d;export const createRoot=__d.createRoot,hydrateRoot=__d.hydrateRoot,flushSync=__d.flushSync,unmountComponentAtNode=__d.unmountComponentAtNode,createPortal=__d.createPortal,render=__d.render,findDOMNode=__d.findDOMNode;`,
  "react-dom/client": `const __c=(typeof window!=="undefined"&&window.__DAYONE_REACT_DOM_CLIENT__)||{};export default __c;export const createRoot=__c.createRoot,hydrateRoot=__c.hydrateRoot;`,
  "react/jsx-runtime": `const __j=(typeof window!=="undefined"&&window.__DAYONE_REACT_JSX_RUNTIME__)||{};export default __j;export const jsx=__j.jsx,jsxs=__j.jsxs,Fragment=__j.Fragment;`,
  "react/jsx-dev-runtime": `const __d=(typeof window!=="undefined"&&window.__DAYONE_REACT_JSX_DEV_RUNTIME__)||{};export default __d;export const jsxDEV=__d.jsxDEV,Fragment=__d.Fragment;`,
};

/** Inline shims for Next.js modules: browser-compatible equivalents for preview. */
const NEXT_LOCAL_SHIMS: Record<string, string> = {
  "next/link": `const R=(typeof window!=="undefined"&&window.__DAYONE_REACT__)||{};
function Link({href,children,className,style,onClick,...rest}){
  function handleClick(e){if(onClick)onClick(e);if(!e.defaultPrevented&&href&&!rest.target){e.preventDefault();window.location.hash="#"+href;}}
  return R.createElement("a",{href:"#"+href,className:className,style:style,onClick:handleClick,...rest},children);
}
export default Link;`,
  "next/image": `const R=(typeof window!=="undefined"&&window.__DAYONE_REACT__)||{};
function Image({src,alt,width,height,className,style,fill,priority,...rest}){
  const s=fill?{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"cover",...(style||{})}:{width:width,height:height,...(style||{})};
  return R.createElement("img",{src:src,alt:alt||"",className:className,style:s,loading:priority?"eager":"lazy",...rest});
}
export default Image;`,
  "next/navigation": `const R=(typeof window!=="undefined"&&window.__DAYONE_REACT__)||{};
export function useRouter(){return{push:function(p){window.location.hash="#"+p},replace:function(p){window.location.hash="#"+p},back:function(){window.history.back()},forward:function(){window.history.forward()},refresh:function(){window.location.reload()},prefetch:function(){}}}
export function usePathname(){var _s=R.useState(window.location.hash.slice(1)||"/"),p=_s[0],setP=_s[1];R.useEffect(function(){function h(){setP(window.location.hash.slice(1)||"/")}window.addEventListener("hashchange",h);return function(){window.removeEventListener("hashchange",h)}},[]);return p}
export function useParams(){return{}}
export function useSearchParams(){return new URLSearchParams(window.location.search)}
export function useSelectedLayoutSegment(){return null}
export function useSelectedLayoutSegments(){return[]}
export function redirect(url){window.location.hash="#"+url}
export function notFound(){throw new Error("Not Found")}`,
  "next/font/google": `export function Inter(){return{className:"font-sans",style:{fontFamily:"Inter,system-ui,sans-serif"}}}
export function Roboto(){return{className:"font-sans",style:{fontFamily:"Roboto,system-ui,sans-serif"}}}
export default function googleFont(){return{className:"",style:{}}}`,
  "next/head": `const R=(typeof window!=="undefined"&&window.__DAYONE_REACT__)||{};
function Head({children}){return null}
export default Head;`,
  "next/script": `const R=(typeof window!=="undefined"&&window.__DAYONE_REACT__)||{};
function Script({src,strategy,...rest}){
  if(src&&typeof document!=="undefined"){var s=document.createElement("script");s.src=src;s.async=true;document.head.appendChild(s)}
  return null;
}
export default Script;`,
  "next/dynamic": `const R=(typeof window!=="undefined"&&window.__DAYONE_REACT__)||{};
export default function dynamic(loader,opts){
  var Comp=null;var P=null;
  function Dynamic(props){
    var _s=R.useState(null),C=_s[0],setC=_s[1];
    R.useEffect(function(){if(!P)P=loader();P.then(function(m){setC(function(){return m.default||m})});},[]);
    if(!C){return opts&&opts.loading?R.createElement(opts.loading):null}
    return R.createElement(C,props);
  }
  return Dynamic;
}`,
  "next/router": `export function useRouter(){return{push:function(p){window.location.hash="#"+p},replace:function(p){window.location.hash="#"+p},back:function(){window.history.back()},pathname:window.location.hash.slice(1)||"/",query:{},asPath:window.location.hash.slice(1)||"/",events:{on:function(){},off:function(){},emit:function(){}}}}
export default{push:function(p){window.location.hash="#"+p},replace:function(p){window.location.hash="#"+p},back:function(){window.history.back()},events:{on:function(){},off:function(){},emit:function(){}}};`,
  "next": `export default {};export const NextPage=undefined;export const GetStaticProps=undefined;export const GetServerSideProps=undefined;export const InferGetStaticPropsType=undefined;`,
};

/** Safe empty-module fallback for any next/* import not in NEXT_LOCAL_SHIMS. */
const NEXT_FALLBACK_SHIM = `export default {};`;

/** Returns true for any bare "next" or "next/*" import. */
function isNextImport(s: string): boolean {
  return s === "next" || s.startsWith("next/");
}

function createSandboxPlugin(files: FileMap, entry: string): esbuildWasm.Plugin {
  const fileSet = new Set(Object.keys(files));
  // Case-insensitive lookup: lowercase key → canonical key from fileSet
  const lowerToCanonical = new Map<string, string>();
  for (const k of fileSet) lowerToCanonical.set(k.toLowerCase(), k);

  const esmUrl = (pkg: string) => {
    if (isJsdelivrRouterFamily(pkg)) {
      return jsdelivrRouterUrl(pkg);
    }
    // Use pinned version if available
    const pkgName = getPackageName(pkg);
    const version = PINNED_VERSIONS[pkgName];
    if (version) {
      return `https://esm.sh/${pkgName}@${version}?bundle&target=es2020&dev`;
    }
    return `https://esm.sh/${pkg}?bundle&target=es2020&dev`;
  };
  const isReactFamily = (s: string) =>
    s === "react" ||
    s === "react-dom" ||
    s === "react-dom/client" ||
    s === "react/jsx-runtime" ||
    s === "react/jsx-dev-runtime";

  const isNextFamily = (s: string) => isNextImport(s);

  const extensions = [".tsx", ".ts", ".jsx", ".js", ".css", ".json"];

  function tryResolve(base: string): string | null {
    // Exact (case-sensitive) first
    if (fileSet.has(base)) return base;
    for (const ext of extensions) {
      if (fileSet.has(base + ext)) return base + ext;
    }
    for (const ext of extensions) {
      const idx = base + "/index" + ext;
      if (fileSet.has(idx)) return idx;
    }
    // Case-insensitive fallback (handles AI casing mismatches like shell.tsx vs Shell.tsx)
    const lower = base.toLowerCase();
    const ci = lowerToCanonical.get(lower);
    if (ci) return ci;
    for (const ext of extensions) {
      const ci2 = lowerToCanonical.get(lower + ext);
      if (ci2) return ci2;
    }
    for (const ext of extensions) {
      const ci3 = lowerToCanonical.get(lower + "/index" + ext);
      if (ci3) return ci3;
    }
    return null;
  }

  function loaderForUrl(
    url: string,
    contentType?: string | null,
  ): esbuildWasm.Loader {
    if (contentType) {
      const ct = contentType.toLowerCase();
      if (ct.includes("typescript")) return "ts";
      if (ct.includes("javascript")) return "js";
      if (ct.includes("css")) return "css";
      if (ct.includes("json")) return "json";
    }
    const path = url.split("?")[0];
    if (path.endsWith(".tsx")) return "tsx";
    if (path.endsWith(".ts")) return "ts";
    if (path.endsWith(".jsx")) return "jsx";
    if (path.endsWith(".mjs") || path.endsWith(".js")) return "js";
    if (path.endsWith(".css")) return "css";
    if (path.endsWith(".json")) return "json";
    return "js";
  }

  return {
    name: "dayone-sandbox",
    setup(build) {
      build.onResolve(
        {
          filter:
            /^(react|react-dom|react-dom\/client|react\/jsx-runtime|react\/jsx-dev-runtime)$/,
        },
        (args) => ({ path: args.path, namespace: "react-local" }),
      );

      build.onLoad({ filter: /.*/, namespace: "react-local" }, (args) => {
        const spec = args.path;
        const contents = REACT_LOCAL_SHIMS[spec];
        if (!contents) throw new Error(`[sandbox] Unknown react-local: ${spec}`);
        return { contents, loader: "js" };
      });

      // Next.js family: resolve ALL next/* imports to browser-compatible shims.
      // Must run BEFORE the catch-all resolver so next/* never hits the allowlist blocker.
      build.onResolve(
        { filter: /^next(\/|$)/ },
        (args) => ({ path: args.path, namespace: "next-local" }),
      );

      build.onLoad({ filter: /.*/, namespace: "next-local" }, (args) => {
        const spec = args.path;
        const contents = NEXT_LOCAL_SHIMS[spec] ?? NEXT_FALLBACK_SHIM;
        return { contents, loader: "js" };
      });

      build.onResolve({ filter: /^dayone:entry$/ }, () => ({
        path: "dayone:entry",
        namespace: "entry",
      }));

      build.onLoad({ filter: /.*/, namespace: "entry" }, () => ({
        contents: `import "./${normalizePath(entry)}";`,
        loader: "ts",
      }));

      function isEsmShReactFamily(urlOrPath: string): boolean {
        const u = urlOrPath.toLowerCase();
        if (!u.includes("esm.sh")) return false;
        return (
          u.includes("/react") ||
          u.includes("/react-dom") ||
          u.includes("jsx-runtime") ||
          u.includes("jsx-dev-runtime") ||
          u.includes("react-refresh")
        );
      }

      function redirectEsmShReactToLocal(urlOrPath: string): esbuildWasm.OnResolveResult {
        const u = urlOrPath.toLowerCase();
        if (u.includes("jsx-dev-runtime")) return { path: "react/jsx-dev-runtime", namespace: "react-local" };
        if (u.includes("jsx-runtime")) return { path: "react/jsx-runtime", namespace: "react-local" };
        if (u.includes("react-dom") && u.includes("/client")) return { path: "react-dom/client", namespace: "react-local" };
        if (u.includes("react-dom")) return { path: "react-dom", namespace: "react-local" };
        return { path: "react", namespace: "react-local" };
      }

      build.onResolve(
        { filter: /esm\.sh/i },
        (args) => {
          if (isEsmShReactFamily(args.path)) {
            return redirectEsmShReactToLocal(args.path);
          }
          return { path: args.path, namespace: "http-url" };
        }
      );

      build.onResolve({ filter: /^https?:\/\// }, (args) => {
        if (isEsmShReactFamily(args.path)) {
          return redirectEsmShReactToLocal(args.path);
        }
        return { path: args.path, namespace: "http-url" };
      });

      build.onResolve({ filter: /^@\// }, (args) => {
        const aliasPath = normalizePath("src/" + args.path.slice(2));
        const exact = tryResolve(aliasPath);
        return { path: exact || aliasPath, namespace: "fs" };
      });

      build.onResolve({ filter: /^~\// }, (args) => {
        const aliasPath = normalizePath("src/" + args.path.slice(2));
        const exact = tryResolve(aliasPath);
        return { path: exact || aliasPath, namespace: "fs" };
      });

      build.onResolve({ filter: /.*/ }, (args) => {
        const pathRaw = String(args.path || "");
        if (isReactFamily(pathRaw)) return { path: pathRaw, namespace: "react-local" };
        if (isNextFamily(pathRaw)) return { path: pathRaw, namespace: "next-local" };
        if (args.namespace === "http-url") {
          if (pathRaw.startsWith(".") || pathRaw.startsWith("/")) {
            try {
              const resolved = new URL(pathRaw, args.importer).href;
              if (isEsmShReactFamily(resolved)) {
                return redirectEsmShReactToLocal(resolved);
              }
              return { path: resolved, namespace: "http-url" };
            } catch {
              // fall through
            }
          }
          if (!isAllowlisted(pathRaw)) {
            const pkg = getPackageName(pathRaw);
            throw new Error(
              `${EXTERNAL_DEPENDENCY_BLOCKED_PREFIX} ${pkg}. Add it to allowlist or vendor it.`,
            );
          }
          return { path: esmUrl(pathRaw), namespace: "http-url" };
        }
        const norm = normalizePath(pathRaw);
        const exact = tryResolve(norm);
        if (exact) return { path: exact, namespace: "fs" };
        if (
          !pathRaw.startsWith(".") &&
          !pathRaw.startsWith("/") &&
          !pathRaw.startsWith("data:")
        ) {
          if (!isAllowlisted(pathRaw)) {
            const pkg = getPackageName(pathRaw);
            throw new Error(
              `${EXTERNAL_DEPENDENCY_BLOCKED_PREFIX} ${pkg}. Add it to allowlist or vendor it.`,
            );
          }
          return { path: esmUrl(pathRaw), namespace: "http-url" };
        }
        const importer = normalizePath(args.importer || "");
        const baseDir = importer.includes("/")
          ? importer.slice(0, importer.lastIndexOf("/"))
          : "";
        const joined = collapsePathDots(
          normalizePath((baseDir ? baseDir + "/" : "") + pathRaw.replace(/^\.\//, "")),
        );
        const resolved = tryResolve(joined);
        return { path: resolved || joined, namespace: "fs" };
      });

      build.onLoad({ filter: /.*/, namespace: "fs" }, (args) => {
        const requested = normalizePath(args.path);
        const key = resolveFileKeyForLoad(requested, files) ?? requested;
        const contents = files[key];
        if (contents == null) throw new Error(`[sandbox] Missing file: ${requested}`);
        return { contents, loader: loaderFor(key) };
      });

      build.onLoad({ filter: /.*/, namespace: "http-url" }, async (args) => {
        let url = args.path;
        if (url.startsWith("http-url:")) url = url.slice("http-url:".length);
        const cached = httpCache.get(url);
        if (cached) return cached;

        // Retry logic for CDN fetch (esm.sh can have transient 500s)
        const MAX_RETRIES = 2;
        const RETRY_DELAY_MS = 1000;
        let lastError: Error | null = null;

        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          try {
            if (attempt > 0) {
              await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
            }
            const res = await fetch(url);
            if (!res.ok) {
              const errorBody = await res.text().catch(() => "");
              const bodySnippet = errorBody.slice(0, 100);
              throw new Error(`HTTP ${res.status}${bodySnippet ? `: ${bodySnippet}` : ""}`);
            }
            const contents = await res.text();
            const contentType = res.headers.get("content-type");
            const loader = loaderForUrl(url, contentType);
            const result = { contents, loader };
            httpCache.set(url, result);
            return result;
          } catch (e: unknown) {
            lastError = e instanceof Error ? e : new Error(String(e));
            // Only retry on 5xx errors or network failures
            const is5xx = lastError.message.includes("HTTP 5");
            const isNetworkError = lastError.message.includes("Failed to fetch") || lastError.message.includes("NetworkError");
            if (!is5xx && !isNetworkError) break;
          }
        }

        // All retries failed - throw user-friendly error
        const pkg = url.includes("esm.sh/") ? url.split("esm.sh/")[1]?.split("?")[0] : url;
        throw new Error(`[sandbox] CDN temporarily unavailable for "${pkg}". Click Refresh to retry.`);
      });
    },
  };
}

export type BuildSuccess = {
  success: true;
  js: string;
  css?: string;
  entry: string;
  /** Paths of screen stubs created in-memory during normalization (debug only). */
  createdScreenStubs?: string[];
};
export type BuildFailure = { success: false; error: string };
export type BuildResult = BuildSuccess | BuildFailure;

/** Fast hash of file map for cache key. */
function hashFileMap(files: FileMap): string {
  let h = 0;
  for (const k of Object.keys(files).sort()) {
    const v = files[k] ?? "";
    for (let i = 0; i < k.length; i++) h = ((h << 5) - h + k.charCodeAt(i)) | 0;
    for (let i = 0; i < v.length; i++) h = ((h << 5) - h + v.charCodeAt(i)) | 0;
  }
  return String(h >>> 0);
}

let lastBuildHash: string | null = null;
let lastBuildResult: BuildSuccess | null = null;

/** Categorize build error for UI (same buckets as generation harness). */
export function getBuildErrorBucket(message: string): string {
  const msg = (message || "").toLowerCase();
  if (/401|429|500|502|503|504|openai|api\.openai/.test(msg)) return "openai";
  if (/external dependency not allowed/i.test(msg)) return "external_dependency";
  if (/missing file|missing files|no entry file|import.*not found|cannot find module/i.test(msg)) return "missing_files";
  if (/sandbox|esbuild|bundle|no js output|build error/i.test(msg)) return "sandbox_bundling";
  if (/referenceerror|typeerror|syntaxerror|react is not defined/i.test(msg)) return "runtime";
  if (/storage|edge function|supabase|failed to fetch|cors|network/i.test(msg)) return "storage_edge";
  return "other";
}

/** Short readable error for UI (no huge stack dumps). */
export function getBuildErrorSnippet(message: string, maxLen = 120): string {
  if (!message) return "";
  const one = message.replace(/\s+/g, " ").trim();
  const firstLine = one.split("\n")[0] ?? one;
  return firstLine.length <= maxLen ? firstLine : firstLine.slice(0, maxLen) + "…";
}

/**
 * Replace Unicode smart/curly quotes with ASCII equivalents.
 * AI models and rich-text pipelines sometimes emit \u201C \u201D \u2018 \u2019
 * which esbuild cannot parse in import statements.
 */
function normalizeSmartQuotes(content: string): string {
  return content
    .replace(/\u201C/g, '"')   // " left double
    .replace(/\u201D/g, '"')   // " right double
    .replace(/\u2018/g, "'")   // ' left single
    .replace(/\u2019/g, "'");  // ' right single
}

/**
 * Fix "Multiple exports with the same name" errors.
 * AI often generates both `export function Foo` and `export default Foo` in the
 * same file, or `export const Foo` + `export default Foo`. esbuild treats this
 * as two exports of "Foo" and fails.
 *
 * Strategy:
 * 1. If `export default function Name` exists, remove any trailing `export default Name;`
 * 2. If `export function Name` AND `export default Name;` exist, convert the named
 *    export to a plain `function Name` and keep the `export default Name;`.
 * 3. If `export const Name` AND `export default Name;` exist, convert the named
 *    export to a plain `const Name` and keep the `export default Name;`.
 */
function fixDuplicateExports(content: string): string {
  // Collect names that have `export default function Name`
  const defaultFnNames = new Set<string>();
  const EXPORT_DEFAULT_FN = /^export\s+default\s+function\s+(\w+)/gm;
  let m: RegExpExecArray | null;
  while ((m = EXPORT_DEFAULT_FN.exec(content)) !== null) {
    defaultFnNames.add(m[1]);
  }

  // If we have `export default function Foo`, remove any `export default Foo;` at the bottom
  for (const name of defaultFnNames) {
    const redundant = new RegExp(`^export\\s+default\\s+${name}\\s*;?\\s*$`, "gm");
    content = content.replace(redundant, "");
  }

  // Find names exported via `export default Name;` (not `export default function`)
  const standaloneDefaults = new Set<string>();
  const STANDALONE_DEFAULT = /^export\s+default\s+(\w+)\s*;?\s*$/gm;
  while ((m = STANDALONE_DEFAULT.exec(content)) !== null) {
    // Skip if it's `export default function` or `export default class`
    if (/^export\s+default\s+(function|class)\b/.test(m[0])) continue;
    standaloneDefaults.add(m[1]);
  }

  // For each standalone `export default Foo;`, check if there's also `export function Foo` or `export const Foo`
  for (const name of standaloneDefaults) {
    const namedExportFn = new RegExp(`^(export)\\s+(function\\s+${name}\\b)`, "gm");
    const namedExportConst = new RegExp(`^(export)\\s+(const\\s+${name}\\b)`, "gm");
    // Remove `export` keyword from the named export, keep the default export
    content = content.replace(namedExportFn, "$2");
    content = content.replace(namedExportConst, "$2");
  }

  return content;
}

/**
 * Normalize smart quotes in ALL TS/JS files in the FileMap, then strip any
 * remaining empty-string imports. Mutates files in-place.
 */
function sanitizeFileMap(files: FileMap): void {
  // Step 0: case-insensitive path dedup (e.g. src/App.tsx vs src/app.tsx)
  // Prefer the canonical-cased version (capital letter in filename)
  const lowerMap = new Map<string, string>(); // lowercase → first seen path
  for (const path of Object.keys(files)) {
    const lower = path.toLowerCase();
    const prev = lowerMap.get(lower);
    if (prev && prev !== path) {
      const prevFile = prev.split("/").pop() ?? "";
      const curFile = path.split("/").pop() ?? "";
      if (/[A-Z]/.test(curFile) && !/[A-Z]/.test(prevFile)) {
        // Current is canonical (App.tsx), drop previous (app.tsx)
        console.warn(`[sandbox] case-conflict: dropping "${prev}" in favor of "${path}"`);
        delete files[prev];
        lowerMap.set(lower, path);
      } else {
        // Previous is canonical, drop current
        console.warn(`[sandbox] case-conflict: dropping "${path}" in favor of "${prev}"`);
        delete files[path];
      }
    } else {
      lowerMap.set(lower, path);
    }
  }

  const EMPTY_FROM = /^[ \t]*(import|export)\b.*?\bfrom\s*["']["'].*$/gm;
  const EMPTY_SIDE = /^[ \t]*import\s*["']["'][ \t]*;?[ \t]*$/gm;

  for (const path of Object.keys(files)) {
    if (!/\.(tsx?|jsx?|mjs)$/.test(path)) continue;
    const raw = files[path];
    if (typeof raw !== "string") continue;

    // Step 1: normalize smart quotes → ASCII
    let content = normalizeSmartQuotes(raw);

    // Step 2: strip empty-string imports (import X from "" / import "")
    EMPTY_FROM.lastIndex = 0;
    EMPTY_SIDE.lastIndex = 0;
    content = content
      .replace(EMPTY_FROM, "// [sanitized] empty import removed")
      .replace(EMPTY_SIDE, "// [sanitized] empty import removed");

    // Step 3: fix duplicate exports ("Multiple exports with the same name")
    content = fixDuplicateExports(content);

    if (content !== raw) {
      console.warn(`[sandbox] sanitizeFileMap: fixed ${path}`);
      files[path] = content;
    }
  }
}

/**
 * Ensure key scaffold files have a default export. AI sometimes rewrites
 * Shell.tsx with only `export function Shell(...)` but App.tsx imports it as
 * a default. This adds `export default Shell;` if missing.
 */
function ensureDefaultExports(files: FileMap): void {
  // Map of file path patterns to the component name that must be default-exported
  // Case-insensitive path match — AI may generate "shell.tsx" instead of "Shell.tsx"
  const MUST_DEFAULT: Array<[RegExp, string]> = [
    [/(?:^|\/)shell\.tsx$/i, "Shell"],
    [/(?:^|\/)sidebar\.tsx$/i, "Sidebar"],
    [/(?:^|\/)layout\.tsx$/i, "Layout"],
    [/(?:^|\/)header\.tsx$/i, "Header"],
  ];

  for (const path of Object.keys(files)) {
    if (!/\.tsx?$/.test(path)) continue;
    const content = files[path];
    if (typeof content !== "string") continue;

    for (const [pattern, name] of MUST_DEFAULT) {
      if (!pattern.test(path)) continue;

      // Already has a default export — nothing to do
      if (/export\s+default\b/.test(content)) break;

      // Has `export function Name` or `export const Name` but no default
      const hasNamedExport = new RegExp(`^export\\s+(function|const)\\s+${name}\\b`, "m").test(content);
      // Also check bare function without export keyword
      const hasBareFunction = !hasNamedExport && new RegExp(`^function\\s+${name}\\b`, "m").test(content);
      if (hasNamedExport || hasBareFunction) {
        files[path] = content + `\nexport default ${name};\n`;
        console.warn(`[sandbox] ensureDefaultExports: added default export to ${path} (${hasBareFunction ? 'bare' : 'named'})`);
      }
      break;
    }
  }
}

/**
 * Normalize file key casing so imports always match stored keys.
 * AI often generates "src/components/shell.tsx" but App.tsx imports from "./components/Shell".
 * This merges mismatched keys into their canonical form (preferring the AI-generated CONTENT).
 */
function canonicalizeFileKeys(files: FileMap): void {
  const CANONICAL: Record<string, string> = {
    "src/app.tsx": "src/App.tsx",
    "src/app.ts": "src/App.tsx",
    "src/app.jsx": "src/App.tsx",
    "src/components/shell.tsx": "src/components/Shell.tsx",
    "src/components/shell.ts": "src/components/Shell.tsx",
    "src/components/shell.jsx": "src/components/Shell.tsx",
    "src/components/ui/tabs.tsx": "src/components/ui/tabs.tsx",
    "src/components/ui/Tabs.tsx": "src/components/ui/tabs.tsx",
  };
  for (const key of Object.keys(files)) {
    const lower = key.toLowerCase();
    const canonical = CANONICAL[lower];
    if (canonical && key !== canonical) {
      // Move content to canonical key (AI content overwrites scaffold)
      files[canonical] = files[key]!;
      delete files[key];
      console.log(`[sandbox] canonicalizeFileKeys: "${key}" → "${canonical}"`);
    }
    // General rule: any path ending in /shell.tsx (any casing) → Shell.tsx
    if (!canonical && /\/shell\.[tj]sx?$/i.test(lower) && !key.endsWith("/Shell.tsx")) {
      const fixed = key.replace(/\/[^/]+$/, "/Shell.tsx");
      files[fixed] = files[key]!;
      delete files[key];
      console.log(`[sandbox] canonicalizeFileKeys: "${key}" → "${fixed}"`);
    }
    // General rule: src/components/ui/ files should be lowercase (scaffold uses lowercase)
    // AI generates Button.tsx, Dialog.tsx, Tabs.tsx but scaffold stores button.tsx, dialog.tsx, tabs.tsx
    if (!canonical && key.startsWith("src/components/ui/") && key !== lower) {
      files[lower] = files[key]!;
      delete files[key];
      console.log(`[sandbox] canonicalizeFileKeys (ui lowercase): "${key}" → "${lower}"`);
    }
  }
}

/** Sandbox build output format: IIFE runs as classic script (no type="module"). */
export const SANDBOX_FORMAT = "iife" as const;

/**
 * Run sandbox build (no DOM). Use for preview and reliability harness.
 * Caches esbuild-wasm init and skips rebuild when file map hash is unchanged.
 * Output is always IIFE; __render must NOT set script.type="module".
 */
export async function buildSandbox(files: FileMap): Promise<BuildResult> {
  const totalStart = performance.now();

  // Start prewarming dependencies if not already started
  startDependencyPrewarm();

  // Canonicalize file key casing FIRST — merges "src/components/shell.tsx" into "src/components/Shell.tsx".
  canonicalizeFileKeys(files);

  // Normalize smart quotes + strip empty imports BEFORE anything else.
  sanitizeFileMap(files);
  // Ensure key layout components have default exports (AI sometimes drops them).
  ensureDefaultExports(files);

  // Nuclear fallback: if Shell.tsx exists but still has no default export, find any function and make it default
  const shellKey = Object.keys(files).find(k => /\/shell\.tsx$/i.test(k));
  if (shellKey && files[shellKey] && !/export\s+default\b/.test(files[shellKey]!)) {
    const shellContent = files[shellKey]!;
    const funcMatch = shellContent.match(/(?:export\s+)?(?:function|const)\s+(\w+)/);
    if (funcMatch) {
      files[shellKey] = shellContent + `\nexport default ${funcMatch[1]};\n`;
      console.warn(`[sandbox] NUCLEAR_SHELL_FIX: force-added default export "${funcMatch[1]}" to ${shellKey}`);
    }
  }

  const initStart = performance.now();
  await ensureEsbuild();
  const sandboxInitMs = Math.round(performance.now() - initStart);

  const normStart = performance.now();
  const { createdScreenStubs } = normalizeFileMapForSandbox(files);
  const normalizeFilesMs = Math.round(performance.now() - normStart);

  const entry = detectEntry(files);
  if (!entry) {
    const keys = Object.keys(files).slice(0, 30).join(", ");
    console.log("[sandbox] build timings:", {
      sandboxInitMs,
      normalizeFilesMs,
      esbuildCompileMs: 0,
      totalBuildMs: Math.round(performance.now() - totalStart),
    });
    return {
      success: false,
      error: `No entry file found (expected src/main.tsx, src/index.tsx, etc). Files present: ${keys}`,
    };
  }

  // Run sanitizer AGAIN after normalization — catches any files added by
  // ensureScreenStubs / ensureComponentStubs / ensureMultiPageLibs
  sanitizeFileMap(files);

  const fileHash = hashFileMap(files);
  if (lastBuildHash === fileHash && lastBuildResult) {
    const totalBuildMs = Math.round(performance.now() - totalStart);
    console.log("[sandbox] build timings (cached):", {
      sandboxInitMs,
      normalizeFilesMs,
      esbuildCompileMs: 0,
      totalBuildMs,
    });
    return lastBuildResult;
  }

  let compileStart = performance.now();
  try {
    const result = await esbuild.build({
      entryPoints: ["dayone:entry"],
      bundle: true,
      write: false,
      format: SANDBOX_FORMAT,
      platform: "browser",
      target: ["es2020"],
      sourcemap: "inline",
      jsx: "automatic",
      jsxImportSource: "react",
      plugins: [createSandboxPlugin(files, entry)],
      define: { global: "globalThis", "process.env.NODE_ENV": '"development"' },
      banner: { js: 'globalThis.process=globalThis.process||{env:{NODE_ENV:"development"}};' },
      logLevel: "silent",
    });
    const esbuildCompileMs = Math.round(performance.now() - compileStart);

    const out = result.outputFiles ?? [];
    const jsOut =
      out.find((f) => f.path.endsWith(".js")) ||
      out.find((f) => !f.path.endsWith(".css")) ||
      null;
    const cssOut = out.find((f) => f.path.endsWith(".css")) ?? null;
    if (!jsOut) {
      console.log("[sandbox] build timings:", {
        sandboxInitMs,
        normalizeFilesMs,
        esbuildCompileMs,
        totalBuildMs: Math.round(performance.now() - totalStart),
      });
      return {
        success: false,
        error: "No JS output from esbuild.build() — bundle produced no JavaScript.",
      };
    }
    const js = jsOut.text;

    // Prepend index.css content to CSS output — stripAllCssImports removes
    // the JS import (esbuild-wasm can't handle CSS-in-JS imports), so we inject
    // the CSS variables + base styles here instead.
    let cssText = cssOut ? new TextDecoder().decode(cssOut.contents) : "";
    const indexCss = files["src/index.css"];
    if (typeof indexCss === "string" && indexCss.trim()) {
      cssText = indexCss + "\n" + cssText;
    }

    const buildResult: BuildSuccess = {
      success: true,
      js,
      css: cssText || undefined,
      entry,
      ...(createdScreenStubs.length > 0 && { createdScreenStubs }),
    };
    lastBuildHash = fileHash;
    lastBuildResult = buildResult;

    const totalBuildMs = Math.round(performance.now() - totalStart);
    console.log("[sandbox] build timings:", {
      sandboxInitMs,
      normalizeFilesMs,
      esbuildCompileMs,
      totalBuildMs,
    });
    return buildResult;
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    const esbuildCompileMs = Math.round(performance.now() - compileStart);
    console.log("[sandbox] build timings:", {
      sandboxInitMs,
      normalizeFilesMs,
      esbuildCompileMs,
      totalBuildMs: Math.round(performance.now() - totalStart),
    });
    return { success: false, error: msg };
  }
}

/**
 * Fetch text content from a URL, using the shared httpCache.
 * Used by buildForDeploy to inline React UMD at build time.
 */
async function fetchTextCached(url: string): Promise<string> {
  const cached = httpCache.get(url);
  if (cached) return cached.contents;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}: HTTP ${res.status}`);
  const text = await res.text();
  httpCache.set(url, { contents: text, loader: "js" });
  return text;
}

/** React UMD production builds — fetched at deploy-build time, inlined into output HTML. */
const REACT_UMD = "https://unpkg.com/react@18.3.1/umd/react.production.min.js";
const REACT_DOM_UMD = "https://unpkg.com/react-dom@18.3.1/umd/react-dom.production.min.js";

/**
 * Inline script that wires React UMD globals into the __DAYONE_REACT__ slots
 * that the sandbox bundle's react-local shims read from.
 * Includes a minimal jsx-runtime implementation (React 18 UMD has no jsx-runtime UMD).
 */
const REACT_GLOBALS_SETUP = `(function(){
var R=window.React,RD=window.ReactDOM;
window.__DAYONE_REACT__=R;
window.__DAYONE_REACT_DOM__=RD;
window.__DAYONE_REACT_DOM_CLIENT__=RD;
function jsx(t,c,k){var p={},ky=null,ref=null;
if(k!==void 0)ky=""+k;
if(c!=null){if(c.key!==void 0)ky=""+c.key;if(c.ref!==void 0)ref=c.ref;
for(var n in c)if(Object.prototype.hasOwnProperty.call(c,n)&&n!=="key"&&n!=="ref"&&n!=="__self"&&n!=="__source")p[n]=c[n]}
if(t&&t.defaultProps)for(var d in t.defaultProps)if(p[d]===void 0)p[d]=t.defaultProps[d];
var owner=null;try{owner=R.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactCurrentOwner.current}catch(e){}
return{$$typeof:Symbol.for("react.element"),type:t,key:ky,ref:ref,props:p,_owner:owner}}
window.__DAYONE_REACT_JSX_RUNTIME__={jsx:jsx,jsxs:jsx,Fragment:R.Fragment};
window.__DAYONE_REACT_JSX_DEV_RUNTIME__={jsxDEV:jsx,Fragment:R.Fragment};
})()`;

/**
 * Build project files into static deploy-ready output.
 *
 * Strategy: call buildSandbox() (the exact same pipeline used for preview) then
 * wrap its JS+CSS output in a self-contained HTML page with React UMD inlined.
 *
 * The sandbox bundle uses react-local shims that read from window.__DAYONE_REACT__
 * globals. In preview, __render.tsx provides these. For deploy, we inline the React
 * UMD production builds and a setup script that wires the globals identically.
 *
 * Result: zero external CDN dependencies, single React instance, same bundle that
 * already works in preview.
 */
export async function buildForDeploy(
  rawFiles: FileMap
): Promise<{ success: true; html: string } | { success: false; error: string }> {
  console.log("buildForDeploy v3 - buildSandbox + inlined React UMD");
  const files: FileMap = { ...rawFiles };

  // Use the exact same build pipeline as preview
  const result = await buildSandbox(files);
  if (!result.success) return { success: false, error: result.error };

  // Fetch React UMD production builds at deploy-build time (cached for subsequent deploys)
  let reactUmd: string;
  let reactDomUmd: string;
  try {
    [reactUmd, reactDomUmd] = await Promise.all([
      fetchTextCached(REACT_UMD),
      fetchTextCached(REACT_DOM_UMD),
    ]);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    return { success: false, error: `Failed to fetch React for deploy: ${msg}` };
  }

  const css = result.css || "";
  const js = result.js;
  console.log(`[buildForDeploy] CSS length=${css.length}, first 200: ${css.slice(0, 200)}`);

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>App</title>
  <script src="https://cdn.tailwindcss.com"><\/script>
  ${css ? `<style>${css}</style>` : ""}
</head>
<body>
  <div id="root"></div>
  <script>${reactUmd}<\/script>
  <script>${reactDomUmd}<\/script>
  <script>${REACT_GLOBALS_SETUP}<\/script>
  <script>${js}<\/script>
</body>
</html>`;

  return { success: true, html };
}
